import type { ErpReport } from "@arena/shared";

export const erpPaymentLabels: Record<string, string> = { PIX: "Pix", CASH: "Dinheiro", DINHEIRO: "Dinheiro", CREDIT: "Cartão de crédito", CREDITO: "Cartão de crédito", DEBIT: "Cartão de débito", DEBITO: "Cartão de débito", CREDIT_CARD: "Cartão de crédito", DEBIT_CARD: "Cartão de débito", CARD: "Cartão", TRANSFER: "Transferência", OTHER: "Outros", MISTO: "Pagamento misto", INFINITEPAY: "InfinitePay", UNKNOWN: "Não informado" };
export const erpStatusLabels: Record<string, string> = { CONFIRMED: "Confirmadas", COMPLETED: "Concluídas", PENDING: "Pendentes", CANCELLED: "Canceladas", NO_SHOW: "Não compareceu" };
export const erpSourceLabels: Record<string, string> = { WEB: "Site", APP: "Aplicativo", MOBILE: "Aplicativo", WHATSAPP: "WhatsApp", ADMIN: "Recepção", MANUAL: "Recepção" };

export function erpDateLabel(value: string, short = false) {
  return new Date(`${value}T12:00:00Z`).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", ...(short ? {} : { year: "numeric" }) });
}

function csvCell(value: string | number) {
  const text = typeof value === "number" ? String(value).replace(".", ",") : value;
  const safe = typeof value === "string" && /^[\s]*[=+@-]/.test(text) ? `'${text}` : text;
  return `"${safe.replaceAll('"', '""')}"`;
}

export function buildErpCsv(report: ErpReport): string {
  const money = (value: number) => value / 100;
  const records: Array<Array<string | number>> = [
    ["Arena Sul Minas — ERP", `${report.period.start} a ${report.period.end}`],
    ["Critérios", "Bar: comandas pagas. Aluguéis: reservas confirmadas e concluídas; não representam recebimento."],
    [],
    ["Indicador", "Período selecionado", "Período anterior"],
    ["Bar recebido (R$)", money(report.current.barSalesCents), money(report.previous.barSalesCents)],
    ["Estornos de caixa (R$)", money(report.current.barRefundsCents), money(report.previous.barRefundsCents)],
    ["Bar recebido menos estornos (R$)", money(report.current.barNetSalesCents), money(report.previous.barNetSalesCents)],
    ["Aluguéis contratados (R$)", money(report.current.rentalBookedCents), money(report.previous.rentalBookedCents)],
    ["Comandas pagas", report.current.paidTabs, report.previous.paidTabs],
    ["Ticket médio do bar (R$)", money(report.current.averageTabCents), money(report.previous.averageTabCents)],
    ["Horas contratadas", report.current.rentalHours, report.previous.rentalHours],
    ["Ocupação (%)", report.current.occupancyPercent, report.previous.occupancyPercent],
    ["Entradas de caixa (R$)", money(report.current.cashIncomeCents), money(report.previous.cashIncomeCents)],
    ["Saídas de caixa (R$)", money(report.current.cashOutflowCents), money(report.previous.cashOutflowCents)],
    ["Movimentação líquida de caixa — não representa lucro (R$)", money(report.current.cashNetMovementCents), money(report.previous.cashNetMovementCents)],
    [],
    ["Data", "Bar recebido (R$)", "Aluguéis contratados (R$)", "Aluguéis concluídos (R$)", "Quantidade de aluguéis"],
    ...report.daily.map((day) => [erpDateLabel(day.date), money(day.barSalesCents), money(day.rentalBookedCents), money(day.rentalCompletedCents), day.rentalCount]),
    [], ["Quadra", "Contratado (R$)", "Concluído (R$)", "Horas reservadas", "Horas disponíveis", "Ocupação (%)"],
    ...report.courts.map((court) => [court.name, money(court.bookedCents), money(court.completedCents), court.bookedHours, court.availableHours, court.occupancyPercent]),
    [], ["Produto", "Quantidade", "Vendas brutas antes de descontos (R$)"],
    ...report.topProducts.map((product) => [product.name, product.quantity, money(product.grossSalesCents)]),
    [], ["Meio de pagamento", "Quantidade de recebimentos", "Recebido (R$)"],
    ...report.paymentMethods.map((payment) => [erpPaymentLabels[payment.method] ?? payment.method, payment.count, money(payment.amountCents)]),
    [], ["Situação das reservas", "Quantidade", "Valor (R$)"],
    ...report.bookingStatuses.map((status) => [erpStatusLabels[status.status] ?? status.status, status.count, money(status.amountCents)]),
    [], ["Origem das reservas", "Quantidade", "Valor (R$)"],
    ...report.bookingSources.map((source) => [erpSourceLabels[source.source] ?? source.source, source.count, money(source.amountCents)]),
    [], ["Dia da semana", "Aluguéis contratados", "Horas contratadas"],
    ...report.weekdayActivity.map((day) => [["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"][day.weekday] ?? day.weekday, day.rentalCount, day.rentalHours]),
    [], ["Operação — posição atual", "Valor", report.generatedAt],
    ["Comandas abertas", report.operations.openTabs],
    ["Comandas abertas — a receber (R$)", money(report.operations.openTabsCents)],
    ["Pedidos em andamento", report.operations.pendingOrders],
    ["Produtos com estoque controlado", report.operations.trackedProducts],
    ["Produtos sem estoque", report.operations.outOfStock],
    ["Estoque avaliado pelo custo (R$)", money(report.operations.stockCostCents)],
  ];
  return `\uFEFF${records.map((record) => record.map(csvCell).join(";")).join("\r\n")}`;
}

export function exportErpCsv(report: ErpReport) {
  const blob = new Blob([buildErpCsv(report)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `arena-erp-${report.period.start}-${report.period.end}.csv`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
